import React, { useContext, useEffect, useMemo } from "react";
import { FieldArray, useFormikContext } from "formik";
import { Field, FormArray, FormGroup, GenericComponent, PInput } from "../../models/models";
import { FormContext } from "../../context/formContext";
import FieldComponent from "./FieldComponent";
import FormGroupCompnent from "./FormGroupComponent";
import { Button } from "primereact/button";
interface FieldArrayProps {
  parentKey: string;
  elementKey: string;
  rest: any;
  label?: string;
  childModel?: any;
  parentValues: any;
  hideExpression?: (formContext: any, values: any, model: any) => boolean;
  fields: (Field | FormGroup | FormArray | PInput | GenericComponent)[];
  autoAddField: boolean;
  mutateValues?: (formContext: any, parentValues: any, childModel: any) => any;
}
export default function FormArrayComponent({
  parentKey,
  elementKey,
  rest,
  parentValues,
  hideExpression,
  label,
  childModel,
  fields,
  autoAddField,
  mutateValues,
}: FieldArrayProps) {
  const context = useContext(FormContext);
  const { values, setFieldValue } = useFormikContext();
  const { removeLabel, addLabel } = rest ?? {};

  function setValue(value: any) {
    setFieldValue(elementKey, value);
  }

  useEffect(() => {
    let initialValues = parentValues[elementKey] ? parentValues[elementKey].map((x: any) => ({ ...childModel, ...x })) : null;

    if (autoAddField && initialValues && mutateValues) {
      initialValues = mutateValues?.(context.formContextStore, initialValues, childModel);
      setValue(initialValues);
    }
  }, [context.formContextStore.loaded]);

  const nextParentKey = useMemo(() => {
    return [parentKey, elementKey].filter((x) => x !== "" && x != null).join(".");
  }, [parentKey, elementKey]);

  const items = useMemo(() => {
    const value = parentValues[elementKey];
    return value;
  }, [elementKey, parentValues]);

  const hidden = useMemo(() => {
    let isHidden = false;
    if (hideExpression != null) {
      isHidden = hideExpression(context.formContextStore, values, parentValues);
    }
    return isHidden;
  }, [hideExpression, values, parentValues, context]);

  return (
    <FieldArray name={nextParentKey}>
      {({ remove, push }) => (
        <div>
          {label && <h4>{label}</h4>}
          {items?.length > 0 &&
            !hidden &&
            items.map((item: any, index: any) => (
              <div key={index} className="flex flex-column">
                {fields.map((f) => {
                  const autoProps = autoAddField && index != 0 ? { disabled: true } : {};
                  switch (f.type) {
                    case "formGroup":
                      f as FormGroup;
                      return (
                        <FormGroupCompnent
                          key={f.key}
                          parentKey={nextParentKey + "." + index}
                          parentValues={items[index]}
                          elementKey={f.key}
                          label={f.label}
                          fields={f.fields}
                          hideExpression={f.hideExpression}
                        />
                      );
                    case "formArray":
                      f as FormArray;
                      return (
                        <FormArrayComponent
                          key={f.key}
                          parentKey={nextParentKey + "." + index}
                          parentValues={items[index]}
                          rest={f.props}
                          elementKey={f.key}
                          label={f.label}
                          fields={f.fields}
                          hideExpression={f.hideExpression}
                          autoAddField={f.autoAddField}
                          mutateValues={f.mutateValues}
                        />
                      );
                    case "field":
                      f as Field;
                      return (
                        <>
                          <FieldComponent
                            key={f.key}
                            parentKey={nextParentKey + "." + index}
                            parentValues={items[index]}
                            elementKey={f.key}
                            fieldType={f.fieldType}
                            label={f.label}
                            warn={f.warn}
                            props={{ ...f.props, index, ...autoProps }}
                            expressionProps={f.expressionProps}
                            hideExpression={f.hideExpression}
                          />
                          <div className={f.props?.advisorytextcss}>{f.props?.advisorytext}</div>
                        </>
                      );

                    case "pInput":
                      f as PInput;
                      return <div className={f.props?.css}>{f.label}</div>;
                      break;
                    case "component": {
                      f as GenericComponent;
                      const Body = f.body;
                      return <Body values={values} parentValues={items[index]} />;
                    }
                  }
                })}
              </div>
            ))}
          {!autoAddField && !context.formContextStore.disabled ? (
            <div>
              <Button type="button" onClick={() => remove(0)} icon="pi pi-minus" className="round-btn p-3 mt-3 m-auto">
                <span className="ml-2">{removeLabel ?? "Remove"}</span>
              </Button>
            </div>
          ) : (
            ""
          )}
          {!autoAddField && !context.formContextStore.disabled ? (
            <Button type="button" icon="pi pi-plus" onClick={() => push(childModel)} className="round-btn p-3 mt-3 m-auto">
              <span className="ml-2">{addLabel ?? "Add"}</span>
            </Button>
          ) : (
            ""
          )}
        </div>
      )}
    </FieldArray>
  );
}
